import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { orderAPI } from '../utils/api';
import { toast } from 'sonner';
import { SEO } from '../seo/SEO';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await orderAPI.getUserOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background pb-8">
      <SEO title="My profile" noIndex includeRestaurantSchema={false} />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">{user.name}</h1>
                <p className="text-muted-foreground">{user.email}</p>
              </div>
              <Button
                data-testid="logout-button"
                variant="outline"
                onClick={handleLogout}
                className="rounded-full"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-4">Your Orders</h2>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground mb-2">No orders yet</p>
              <p className="text-sm text-muted-foreground mb-4">Start ordering some delicious food!</p>
              <Button onClick={() => navigate('/')} className="rounded-full">
                Browse Menu
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-mono text-sm text-muted-foreground">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(order.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">£{order.total_amount.toFixed(2)}</p>
                      <span className="inline-block mt-1 px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary capitalize">
                        {order.order_status}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    {order.items.map((item, index) => (
                      <p key={index} className="text-sm">
                        {item.name} x {item.quantity}
                      </p>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => navigate(`/order-tracking/${order.id}`)}
                  >
                    Track Order
                  </Button>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
